import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from 'aws-amplify/api';
import { uploadData } from 'aws-amplify/storage';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import '../styles.css';

interface MenuAnalysisResult {
  recommendedDish: {
    name: string;
    explanation: string;
    estimatedCalories: number;
  };
  worstChoice: {
    name: string;
    explanation: string;
    estimatedCalories: number;
  };
  allDishes: Array<{
    name: string;
    estimatedCalories: number;
  }>;
}

const MenuAnalysis: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<MenuAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Initialize camera when component mounts
  useEffect(() => {
    if (!cameraActive) return;
    
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }, // Use back camera if available
          audio: false
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Failed to access your camera. Please make sure you have granted camera permissions.');
        setCameraActive(false);
      }
    };

    startCamera();

    // Store ref value to use in cleanup
    const currentVideo = videoRef.current;

    // Cleanup when component unmounts or camera is turned off
    return () => {
      const stream = currentVideo?.srcObject as MediaStream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, [cameraActive]);

  const handleCaptureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageDataURL = canvas.toDataURL('image/jpeg');
      setCapturedImage(imageDataURL);
      
      const stream = video.srcObject as MediaStream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
      }
      setCameraActive(false);
    }
  };

  const handleAnalyzeCapturedImage = async () => {
    if (!capturedImage) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const user = await getCurrentUser();
      const userId = user.userId;
      const blob = await fetch(capturedImage).then(res => res.blob());
      const fileName = `${userId}/${Date.now()}.jpg`;
      
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      await uploadData({
        key: fileName,
        data: blob,
        options: {
          contentType: 'image/jpeg',
          accessLevel: 'private'
        }
      }).result;     
      
      
      const storageKey = "private/" + session.identityId + "/" + fileName;
      const restOperation = post({
        apiName: 'FoodTrackerAPI',
        path: '/food/analyze-menu',
        options: {
          body: { storageKeys: [storageKey] },
          headers: {
            Authorization: authToken
          }
        }
      });

      const response = await restOperation.response;
      const responseData = JSON.parse(await response.body.text()) as { analysis: MenuAnalysisResult };
      setAnalysis(responseData.analysis);
      setShowCamera(false);
      setCapturedImage(null);
    } catch (err) {
      console.error('Error analyzing menu:', err);
      setError('Failed to analyze menu. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCameraActive(true);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      // Get current authenticated user and session
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();

      if (!authToken) {
        throw new Error('Not authenticated');
      }

      // Get credentials from Cognito Identity Pool
      await fetchAuthSession(); // Refresh the session

      // Upload the image to S3
      const file = files[0]; // Only process the first file
      const fileName = `${user.userId}/${Date.now()}-${file.name}`;
      
      console.log('Uploading to S3...');
      try {
        await uploadData({
          key: fileName,
          data: file,
          options: {
            contentType: file.type,
            accessLevel: 'private',
            onProgress: ({ transferredBytes, totalBytes }: { transferredBytes: number; totalBytes: number }) => {
              const percentComplete = Math.round((transferredBytes / totalBytes) * 100);
              console.log(`Upload progress: ${percentComplete}%`);
            }
          }
        }).result;
        
        console.log('File uploaded successfully');
       
        console.log('Proceeding with analysis');
        
        // Add the full storage key path
        const storageKey = "private/" + session.identityId + "/" + fileName;

        // Analyze the menu
        const restOperation = post({
          apiName: 'FoodTrackerAPI',
          path: '/food/analyze-menu',
          options: {
            body: { storageKeys: [storageKey] },
            headers: {
              Authorization: authToken
            }
          }
        });

        const response = await restOperation.response;
        const responseData = JSON.parse(await response.body.text()) as { analysis: MenuAnalysisResult };
        setAnalysis(responseData.analysis);
      } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
      }
    } catch (err) {
      console.error('Error analyzing menu:', err);
      setError('Failed to analyze menu. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderTips = () => (
    <div className="camera-tips" style={{ marginTop: '20px', fontSize: '0.9rem' }}>
      <h4>Tips for best results:</h4>
      <ul>
        <li>Make sure the menu is well-lit and clearly visible</li>
        <li>Avoid glare or shadows on the menu</li>
        <li>Try to capture the entire menu section in one shot</li>
      </ul>
    </div>
  );

  return (
    <div>
    
      {!showCamera && !analysis && (
        <div className="card" style={{ height: '100%', maxHeight: '100vh', overflowY: 'hidden', margin: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p>Take a photo or select an image of your menu to analyze</p>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <span className="loading-spinner" style={{ width: '20px', height: '20px', marginRight: '10px' }}></span>
                Analyzing...
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                <button onClick={() => {
                  setShowCamera(true);
                  setCameraActive(true);
                  setCapturedImage(null);
                  setError(null);
                }} className="btn">
                  Open Camera
                </button>
                <button onClick={() => fileInputRef.current?.click()} className="btn btn-secondary">
                  Upload Image
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            {renderTips()}
          </div>
        </div>
      )}

      {showCamera && (
        <div className="card" style={{ height: '100%', maxHeight: '100vh', overflowY: 'hidden', margin: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="camera-container" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {cameraActive && (
              <>
                <div style={{ flex: 1, position: 'relative' }}>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    className="camera-view"
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <button 
                  onClick={handleCaptureImage} 
                  className="btn"
                  style={{ 
                    margin: '16px',
                    height: '48px',
                    fontSize: '16px'
                  }}
                >
                  Take Photo
                </button>
                {renderTips()}
              </>
            )}
            {!cameraActive && capturedImage && (
              <>
                <img 
                  src={capturedImage} 
                  alt="Captured menu" 
                  style={{ display: 'block', width: '100%', borderRadius: '8px' }} 
                />
                <div className="camera-btn-container" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                  <button
                    onClick={handleRetake}
                    disabled={loading}
                    className="btn btn-secondary"
                  >
                    Retake
                  </button>
                  <button
                    onClick={handleAnalyzeCapturedImage}
                    disabled={loading}
                    className="btn"
                  >
                    {loading ? (
                      <>
                        <span className="loading-spinner" style={{ width: '20px', height: '20px', marginRight: '10px' }}></span>
                        Analyzing...
                      </>
                    ) : 'Analyze Menu'}
                  </button>
                </div>
              </>
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {analysis && (
        <div style={{ padding: '20px' }}>
          <div className="card">
            <h3 className="card-title">
              Recommended Dish
            </h3>
            <h4 style={{ margin: '1rem 0' }}>{analysis.recommendedDish.name}</h4>
            <p style={{ color: 'var(--logo-grey)' }}>{analysis.recommendedDish.explanation}</p>
            <p style={{ color: 'var(--logo-grey)', marginTop: '0.5rem' }}>{analysis.recommendedDish.estimatedCalories} calories</p>
          </div>

          <div className="card">
            <h3 className="card-title">
              Not Recommended
            </h3>
            <h4 style={{ margin: '1rem 0' }}>{analysis.worstChoice.name}</h4>
            <p style={{ color: 'var(--logo-grey)' }}>{analysis.worstChoice.explanation}</p>
            <p style={{ color: 'var(--logo-grey)', marginTop: '0.5rem' }}>{analysis.worstChoice.estimatedCalories} calories</p>
          </div>

          <div className="card">
            <h3 className="card-title">
              All Dishes
            </h3>
            <div className="dishes-list" style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              overflowY: 'auto',
              paddingRight: '10px'
            }}>
              {analysis.allDishes.map((dish, index) => (
                <div key={index} className="dish-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{dish.name}</span>
                  <span style={{ color: 'var(--logo-grey)' }}>{dish.estimatedCalories} calories</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuAnalysis;
